import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Dialog } from "../../Catalyst/dialog";
import { Button } from "../../Catalyst/button";
import { useToast } from "../../../contexts/ToastContext";
import { uploadOrganizationBenefits } from "../../../services/OrganizationService";
import UploadIcon from "../../../assets/UploadIcon";
import PdfFileIcon from "../../../assets/PdfFileIcon";
import DeleteIcon from "../../../assets/DeleteIcon";
import ErrorIcon from "../../../assets/ErrorIcon";

// The benefits Q&A pipeline indexes a PDF, so the picker only accepts PDFs.
const ACCEPTED_EXTENSION = ".pdf";
const ACCEPTED_MIME_TYPE = "application/pdf";
// Client-side guard so an oversized file fails fast instead of after a long upload.
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ButtonSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const validateFile = (file: File): string => {
  const isPdf =
    file.type === ACCEPTED_MIME_TYPE || file.name.toLowerCase().endsWith(ACCEPTED_EXTENSION);
  if (!isPdf) return "Only PDF files are supported.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_FILE_SIZE_BYTES) return `The file must be smaller than ${MAX_FILE_SIZE_MB} MB.`;
  return "";
};

interface UploadBenefitsDialogProps {
  orgId: number;
  orgName: string;
  open: boolean;
  onClose: () => void;
}

const UploadBenefitsDialog = ({ orgId, orgName, open, onClose }: UploadBenefitsDialogProps) => {
  const { showSuccessToast, showErrorToast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Start from a clean slate every time the dialog opens.
  useEffect(() => {
    if (open) {
      setFile(null);
      setValidationError("");
      setIsDragging(false);
      setUploading(false);
    }
  }, [open]);

  const selectFile = (selected?: File | null) => {
    if (!selected) return;
    const error = validateFile(selected);
    if (error) {
      setFile(null);
      setValidationError(error);
      return;
    }
    setFile(selected);
    setValidationError("");
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
    // Reset the input so re-picking the same file still fires onChange.
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleClose = () => {
    // Closing mid-upload would leave the request running with no way to report it.
    if (uploading) return;
    onClose();
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      await uploadOrganizationBenefits(orgId, file);
      showSuccessToast(
        "Benefits package uploaded",
        `${file.name} is now the benefits package for ${orgName}. It replaces any package previously uploaded for this organization.`
      );
      onClose();
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        data?.msg ||
        data?.message ||
        data?.error ||
        "We couldn't upload the benefits package. Please try again.";
      showErrorToast("Upload failed", message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog size="2xl" open={open} onClose={handleClose}>
      <div className="flex items-start justify-between gap-4">
        <h2 className="desktext10 text-texthead">Upload Benefit Package for {orgName}</h2>
        <button
          type="button"
          onClick={handleClose}
          disabled={uploading}
          aria-label="Close"
          className="shrink-0 rounded-md p-1 text-zinc-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-brColor p-4">
        <h3 className="font-inter text-sm font-medium leading-5 text-[#525252]">
          Upload your Benefit
        </h3>
        <p className="mt-1 font-inter text-xs font-medium leading-5 text-[#525252]">
          Upload a new benefits document for this organization. This will overwrite and replace any
          existing package.
        </p>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            if (!uploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`mt-3 flex flex-col items-center justify-center rounded-xl border-2 p-8 transition ${
            isDragging ? "border-green-500 bg-green-50" : ""
          } ${validationError ? "border-red-500" : "border-gray-200 bg-white"}`}
        >
          {!file ? (
            <label
              htmlFor="benefits-upload"
              className="flex cursor-pointer flex-col items-center text-center"
            >
              <div className="mb-4 rounded-lg border border-shadowborder p-2 shadow-light-sm">
                <UploadIcon />
              </div>
              <input
                id="benefits-upload"
                type="file"
                accept={`${ACCEPTED_EXTENSION},${ACCEPTED_MIME_TYPE}`}
                className="sr-only"
                onChange={handleFileChange}
                disabled={uploading}
              />
              <p className="text8 mb-1">
                <span className="desktext13 text-green-500">Click to upload</span> or drag and drop
              </p>
              <p className="text8 text-gray-500">PDF (max {MAX_FILE_SIZE_MB} MB)</p>
            </label>
          ) : (
            <div className="flex w-full items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <PdfFileIcon />
                <div className="flex min-w-0 flex-col">
                  <div className="desktext15 truncate text-textColor">{file.name}</div>
                  <div className="desktext15 text-textColor3">
                    {(file.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                disabled={uploading}
                aria-label="Remove selected file"
                className="text-sm text-gray-500 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <DeleteIcon />
              </button>
            </div>
          )}
        </div>

        {validationError && (
          <div className="mt-3 flex items-center justify-center text-sm text-red-600">
            <div className="mr-3">
              <ErrorIcon />
            </div>
            {validationError}
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center justify-end gap-3">
        <Button outline onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button color="dark" onClick={handleUpload} disabled={!file || uploading}>
          {uploading && <ButtonSpinner />}
          {uploading ? "Uploading..." : "Upload Package"}
        </Button>
      </div>
    </Dialog>
  );
};

export default UploadBenefitsDialog;
